import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  ClickAwayListener,
  Divider,
  Drawer,
  Popper,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { usePermissions } from 'hooks';
import { getStorageItem, setStorageItem } from 'utils';

// Containers
import * as CommandPalette from 'containers/common/CommandPalette';
import { useSpotlightTarget } from 'containers/common/Spotlight';

// Components
import { Mui, PerfectScrollbar } from 'components';

// Local Components
import { findActiveCategoryIndex, hasActiveDescendant } from './activeCategory';
import { DRAWER_WIDTH } from './constants';
import {
  buildMenuKey,
  FAVORITES_STORAGE_KEY,
  getFavoriteDisplay,
  getFavoriteEntries,
  isItemVisible,
  sanitizeFavoriteKeys,
} from './menuFavorites';
import NavItem from './NavItem';
import { renderLeafItem, renderNavItems } from './renderNavItems';
import { useStyles } from './styles';
import useCategoryFlyout from './useCategoryFlyout';

const MENU_STORAGE_KEY = '@eskolare/menu-sections';
const SUBMENU_STORAGE_KEY = '@eskolare/menu-submenus';

const FLYOUT_POPPER_MODIFIERS = {
  flip: { enabled: true, flipVariations: true },
  preventOverflow: { enabled: true, boundariesElement: 'viewport' },
  offset: { enabled: true, offset: '0, 8' },
};

const SUBFLYOUT_POPPER_MODIFIERS = {
  ...FLYOUT_POPPER_MODIFIERS,
  offset: { enabled: true, offset: '0, 0' },
};

const CATEGORY_ICONS = {
  'Minha conta': 'account_circle',
  Outros: 'apps',
  'Programa de pontos': 'loyalty',
  'Eskolare Insights': 'assessment',
  'Pessoas e acesso': 'people',
  'Suporte da plataforma': 'help',
  'Suporte aos clientes': 'headset_mic',
  Gestão: 'dashboard',
  'Mensalidades e matrículas': 'school',
  Relacionamento: 'contacts',
  Loja: 'store',
  Promoções: 'local_offer',
  Configurações: 'settings',
  Catálogo: 'category',
  Cadastros: 'assignment',
};

function NavBar(props) {
  const {
    openMobile,
    onMobileClose,
    profile = {},
    items,
    children,
    isNavCollapsed = false,
    onToggleCollapse,
  } = props;

  // Hooks
  const location = useLocation();
  const { sellerSlug, institutionSlug } = useParams();
  const { permissions } = usePermissions();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const collapseSpotlightRef = useSpotlightTarget('nav-collapse-2026-08');

  // Constants
  const railActive = isNavCollapsed && !fullScreen;
  const classes = useStyles({ isNavCollapsed: railActive });

  const { info, cover } = profile || {};
  const { isStaff } = permissions || {};

  let navContext = null;
  if (sellerSlug) navContext = 'sellers';
  else if (institutionSlug) navContext = 'institutions';
  const accentColor = navContext ? theme.palette.context[navContext]?.main : undefined;

  const categoryTextColor = theme.palette.text.secondary;
  const categoryAccents = [
    { bg: theme.palette.brand.grey[200], text: categoryTextColor },
    { bg: theme.palette.brand.grey[100], text: categoryTextColor },
  ];
  const categoryActiveColorByContext = {
    sellers: theme.palette.brand.green[400],
    institutions: theme.palette.brand.blue[400],
  };
  const categoryActiveColor =
    categoryActiveColorByContext[navContext] ?? theme.palette.primary.main;

  const visibleItems = useMemo(
    () => items.filter(item => isItemVisible(item, isStaff)),
    [items, isStaff],
  );

  const activeCategoryKey = useMemo(() => {
    const bestIndex = findActiveCategoryIndex(visibleItems, location.pathname);
    if (bestIndex === -1) return null;

    return [visibleItems[bestIndex].subheader || 'subheader', bestIndex.toString()].join('-');
  }, [visibleItems, location.pathname]);

  // States
  const [closedMenuSections, setClosedMenuSections] = useState(() =>
    getStorageItem(MENU_STORAGE_KEY, []),
  );
  const [submenuPrefs, setSubmenuPrefs] = useState(() => getStorageItem(SUBMENU_STORAGE_KEY, {}));
  const [favoriteKeys, setFavoriteKeys] = useState(() => {
    const storedFavoriteKeys = getStorageItem(FAVORITES_STORAGE_KEY, []);
    const visible = items.filter(item => isItemVisible(item, isStaff));

    return sanitizeFavoriteKeys(storedFavoriteKeys, visible, isStaff);
  });

  const favoriteEntries = useMemo(
    () => getFavoriteEntries(favoriteKeys, visibleItems, isStaff),
    [favoriteKeys, visibleItems, isStaff],
  );

  const {
    flyout,
    closeNow: closeFlyoutNow,
    cancelClose: cancelFlyoutClose,
    handleMouseEnter: handleFlyoutMouseEnter,
    handleMouseLeave: handleFlyoutMouseLeave,
    handleClick: handleFlyoutClick,
    handleKeyDown: handleFlyoutKeyDown,
  } = useCategoryFlyout(`${location.pathname}|${railActive}`);
  const flyoutKey = flyout?.key ?? null;

  const flyoutActiveBranchKeys = useMemo(() => {
    if (!flyout) return new Set();

    return new Set(
      flyout.config.items
        .filter(item => item.items && hasActiveDescendant(item.items, location.pathname))
        .map(item =>
          buildMenuKey({
            sectionKey: flyout.key,
            parentKey: '',
            title: item.title,
            id: item.menuKey,
          }),
        ),
    );
  }, [flyout, location.pathname]);

  const {
    flyout: subFlyout,
    closeNow: closeSubFlyoutNow,
    cancelClose: cancelSubFlyoutClose,
    handleMouseEnter: handleSubFlyoutMouseEnter,
    handleMouseLeave: handleSubFlyoutMouseLeave,
    handleClick: handleSubFlyoutClick,
  } = useCategoryFlyout(`${location.pathname}|${railActive}|${flyoutKey ?? ''}`);
  const subFlyoutKey = subFlyout?.key ?? null;

  // Actions
  const handleFavoriteToggle = menuKey => {
    const next = favoriteKeys.includes(menuKey)
      ? favoriteKeys.filter(key => key !== menuKey)
      : [...favoriteKeys, menuKey];

    setFavoriteKeys(next);
    setStorageItem(FAVORITES_STORAGE_KEY, next);
  };

  const handleSubmenuToggle = (menuKey, isOpen) => {
    const next = { ...submenuPrefs, [menuKey]: isOpen };

    setSubmenuPrefs(next);
    setStorageItem(SUBMENU_STORAGE_KEY, next);
  };

  const handleCloseMenuSection = key => {
    const next = closedMenuSections.includes(key)
      ? closedMenuSections.filter(item => item !== key)
      : [...closedMenuSections, key];

    setClosedMenuSections(next);
    setStorageItem(MENU_STORAGE_KEY, next);
  };

  const renderRailSectionHeader = (config, index, key) => {
    const accent =
      key === activeCategoryKey
        ? { bg: categoryActiveColor, text: theme.palette.brand.grey[0] }
        : categoryAccents[index % categoryAccents.length];
    const categoryIcon = CATEGORY_ICONS[config.subheader];

    return (
      <Mui.ListSubheader disableGutters disableSticky>
        <Mui.Box
          display="flex"
          justifyContent="center"
          className={classes.railHeaderRow}
          tabIndex={0}
          role="button"
          aria-haspopup
          aria-expanded={flyoutKey === key}
          data-flyout-trigger
          onMouseEnter={event => handleFlyoutMouseEnter(key, config, event)}
          onMouseLeave={handleFlyoutMouseLeave}
          onClick={event => handleFlyoutClick(key, config, event)}
          onKeyDown={event => handleFlyoutKeyDown(key, config, event)}>
          <Mui.Badge
            invisible={config.isStaff || !config.isNew}
            variant="dot"
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}>
            <span
              className={classes.railSectionAvatar}
              style={{ backgroundColor: accent.bg, color: accent.text }}>
              {categoryIcon ? (
                <Mui.Icon fontSize="inherit">{categoryIcon}</Mui.Icon>
              ) : (
                typeof config.subheader === 'string' && config.subheader.charAt(0)
              )}
            </span>
          </Mui.Badge>
        </Mui.Box>
      </Mui.ListSubheader>
    );
  };

  const renderSectionHeader = (config, key, interactive = true) => (
    <Mui.ListSubheader
      disableGutters
      disableSticky
      className={!interactive ? classes.flyoutSectionHeader : undefined}
      onClick={interactive ? () => handleCloseMenuSection(key) : undefined}>
      <Mui.Box
        display="flex"
        alignItems="center"
        justifyContent={interactive ? 'space-between' : 'center'}>
        <Mui.Typography
          variant="subtitle2"
          className={clsx(classes.menuSection, !interactive && classes.flyoutSectionTitle)}>
          {config.subheader}{' '}
          {config.isStaff && <Mui.Chip size="small" label="STAFF" className={classes.staffChip} />}
          {!config.isStaff && config.isNew && (
            <Mui.Chip
              size="small"
              label="NOVO"
              color="primary"
              style={accentColor ? { backgroundColor: accentColor } : undefined}
            />
          )}
        </Mui.Typography>
        {interactive &&
          (closedMenuSections.includes(key) ? (
            <ExpandMoreIcon color="inherit" />
          ) : (
            <ExpandLessIcon color="inherit" />
          ))}
      </Mui.Box>
    </Mui.ListSubheader>
  );

  const renderFlyoutBranchRow = (item, sectionKey, index) => {
    const menuKey = buildMenuKey({
      sectionKey,
      parentKey: '',
      title: item.title,
      id: item.menuKey,
    });

    return (
      <NavItem
        isBranch
        submenuIndicator="flyout"
        depth={0}
        icon={item.icon}
        key={[index, 0].join('-')}
        info={item.info}
        open={subFlyoutKey === menuKey}
        title={item.title}
        isNew={item.isNew}
        isStaff={item.isStaff}
        accentColor={accentColor}
        isActive={flyoutActiveBranchKeys.has(menuKey)}
        onToggle={event => handleSubFlyoutClick(menuKey, item, event)}
        onMouseEnter={event => handleSubFlyoutMouseEnter(menuKey, item, event)}
        onMouseLeave={handleSubFlyoutMouseLeave}
      />
    );
  };

  const renderTier1FlyoutItems = (categoryItems, sectionKey) => (
    <Mui.List disablePadding>
      {categoryItems
        .filter(item => isItemVisible(item, isStaff))
        .map((item, index) => {
          if (item.items) return renderFlyoutBranchRow(item, sectionKey, index);

          const menuKey = buildMenuKey({
            sectionKey,
            parentKey: '',
            title: item.title,
            id: item.menuKey,
          });
          return renderLeafItem({
            item,
            depth: 0,
            index,
            menuKey,
            favoriteKeys,
            onFavoriteToggle: handleFavoriteToggle,
            accentColor,
            railMode: false,
          });
        })}
    </Mui.List>
  );

  const renderFavoritesHeader = () => (
    <Mui.ListSubheader disableGutters disableSticky>
      <Mui.Box
        display="flex"
        alignItems="center"
        justifyContent={railActive ? 'center' : 'flex-start'}
        className={railActive ? classes.railHeaderRow : undefined}>
        <Mui.Icon className={classes.favoritesStar}>star</Mui.Icon>
        {!railActive && (
          <Mui.Typography variant="subtitle2" className={classes.favoritesHeader}>
            Favoritos
          </Mui.Typography>
        )}
        {railActive && <Divider className={classes.railFavoritesDivider} />}
      </Mui.Box>
    </Mui.ListSubheader>
  );

  // Effects
  useEffect(() => {
    const sanitized = sanitizeFavoriteKeys(favoriteKeys, visibleItems, isStaff);
    if (sanitized.length !== favoriteKeys.length) {
      setFavoriteKeys(sanitized);
      setStorageItem(FAVORITES_STORAGE_KEY, sanitized);
    }
  }, [favoriteKeys, visibleItems, isStaff]);

  useEffect(() => {
    if (openMobile && onMobileClose) onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const coverStyle = {
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    background: `linear-gradient(to top, white, white, white, white, rgba(255, 255, 255, 0.6)), url(${cover})`,
  };

  const content = (
    <Mui.Box
      height="100%"
      display="flex"
      flexDirection="column"
      style={cover && !railActive ? coverStyle : undefined}>
      <Mui.Box
        flex={1}
        minHeight={0}
        className={railActive ? classes.railScrollWrapper : undefined}>
        <PerfectScrollbar options={{ suppressScrollX: true }}>
          {children}
          {info && !railActive && (
            <>
              <Mui.Box p={2} py={4}>
                <Mui.Typography variant="body2" color="textSecondary" align="center" noWrap>
                  {info}
                </Mui.Typography>
              </Mui.Box>
              <Divider />
            </>
          )}
          <Mui.Hidden lgUp>
            {railActive ? (
              <Mui.Box display="flex" justifyContent="center" pt={1.25}>
                <Mui.Tooltip title="Pesquisar">
                  <Mui.IconButton
                    onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
                    aria-label="Pesquisar">
                    <Mui.Icon>search</Mui.Icon>
                  </Mui.IconButton>
                </Mui.Tooltip>
              </Mui.Box>
            ) : (
              <Mui.Box px={2} pt={2}>
                <CommandPalette.Trigger fullWidth />
              </Mui.Box>
            )}
          </Mui.Hidden>
          <Mui.Box p={2}>
            {favoriteEntries.some(entry => entry.href) && (
              <Mui.List subheader={renderFavoritesHeader()}>
                {favoriteEntries
                  .filter(entry => entry.href)
                  .map(entry => {
                    const display = getFavoriteDisplay(entry);

                    return (
                      <NavItem
                        key={entry.menuKey}
                        depth={0}
                        href={entry.href}
                        target={entry.target}
                        icon={
                          display.useChevronIcon
                            ? newProps => <Mui.Icon {...newProps}>chevron_right</Mui.Icon>
                            : display.icon
                        }
                        disabled={entry.disabled}
                        info={entry.info}
                        title={display.title}
                        isNew={entry.isNew}
                        isStaff={entry.isStaff}
                        favoriteable
                        isFavorite
                        onFavoriteToggle={() => handleFavoriteToggle(entry.menuKey)}
                        accentColor={accentColor}
                        railMode={railActive}
                      />
                    );
                  })}
              </Mui.List>
            )}
            {visibleItems.map((config, index) => {
              const key = [config.subheader || 'subheader', index.toString()].join('-');
              if (Array.isArray(config.items) && config.items.length < 1) return null;
              return (
                <Mui.List
                  key={key}
                  className={classes.menuSectionHeader}
                  disablePadding={railActive}
                  style={railActive ? { marginBottom: 4 } : undefined}>
                  <div style={{ display: railActive ? 'block' : 'none' }}>
                    {renderRailSectionHeader(config, index, key)}
                  </div>
                  <div style={{ display: railActive ? 'none' : 'block' }}>
                    {renderSectionHeader(config, key)}
                    <Mui.Collapse in={!closedMenuSections.includes(key)}>
                      {renderNavItems({
                        isStaff,
                        items: config.items,
                        pathname: location.pathname,
                        sectionKey: key,
                        submenuPrefs,
                        onSubmenuToggle: handleSubmenuToggle,
                        favoriteKeys,
                        onFavoriteToggle: handleFavoriteToggle,
                        accentColor,
                      })}
                    </Mui.Collapse>
                  </div>
                </Mui.List>
              );
            })}
          </Mui.Box>
        </PerfectScrollbar>
      </Mui.Box>
      {flyout && (
        <Popper
          open
          anchorEl={flyout.anchor}
          placement="right-start"
          modifiers={FLYOUT_POPPER_MODIFIERS}
          style={{ zIndex: theme.zIndex.drawer + 1 }}>
          <ClickAwayListener
            onClickAway={event => {
              if (event.target.closest?.('[data-flyout-trigger]')) return;
              closeFlyoutNow();
            }}>
            <Mui.Paper
              elevation={8}
              style={{ width: DRAWER_WIDTH }}
              onMouseEnter={cancelFlyoutClose}
              onMouseLeave={handleFlyoutMouseLeave}
              onKeyDown={event => {
                if (event.key === 'Escape') closeFlyoutNow();
              }}>
              {renderSectionHeader(flyout.config, flyout.key, false)}
              <Mui.Collapse in>
                <div className={classes.flyoutItemList}>
                  {renderTier1FlyoutItems(flyout.config.items, flyout.key)}
                </div>
              </Mui.Collapse>
            </Mui.Paper>
          </ClickAwayListener>
        </Popper>
      )}
      {subFlyout && (
        <Popper
          open
          anchorEl={subFlyout.anchor}
          placement="right-start"
          modifiers={SUBFLYOUT_POPPER_MODIFIERS}
          style={{ zIndex: theme.zIndex.drawer + 2 }}>
          <ClickAwayListener
            onClickAway={event => {
              if (event.target.closest?.('[data-flyout-trigger]')) return;
              closeSubFlyoutNow();
            }}>
            <Mui.Paper
              elevation={8}
              data-flyout-trigger
              className={classes.subFlyoutPaper}
              style={{ width: DRAWER_WIDTH }}
              onMouseEnter={() => {
                cancelSubFlyoutClose();
                cancelFlyoutClose();
              }}
              onMouseLeave={() => {
                handleSubFlyoutMouseLeave();
                handleFlyoutMouseLeave();
              }}
              onKeyDown={event => {
                if (event.key === 'Escape') closeSubFlyoutNow();
              }}>
              {renderSectionHeader(
                {
                  subheader: subFlyout.config.title,
                  isStaff: subFlyout.config.isStaff,
                  isNew: subFlyout.config.isNew,
                },
                subFlyoutKey,
                false,
              )}
              <Mui.Collapse in>
                <div className={classes.flyoutItemList}>
                  {renderNavItems({
                    isStaff,
                    items: subFlyout.config.items,
                    pathname: location.pathname,
                    sectionKey: flyout?.key,
                    parentKey: subFlyoutKey,
                    submenuPrefs,
                    onSubmenuToggle: handleSubmenuToggle,
                    favoriteKeys,
                    onFavoriteToggle: handleFavoriteToggle,
                    accentColor,
                  })}
                </div>
              </Mui.Collapse>
            </Mui.Paper>
          </ClickAwayListener>
        </Popper>
      )}
      {!fullScreen && onToggleCollapse && (
        <>
          <Divider />
          <Mui.Box
            ref={collapseSpotlightRef}
            p={1}
            display="flex"
            justifyContent={railActive ? 'center' : 'flex-start'}>
            {railActive ? (
              <Mui.IconButton onClick={onToggleCollapse} aria-label="Expandir menu">
                <Mui.Icon>chevron_right</Mui.Icon>
              </Mui.IconButton>
            ) : (
              <Mui.Button
                variant="text"
                color="default"
                onClick={onToggleCollapse}
                startIcon={<Mui.Icon>chevron_left</Mui.Icon>}>
                Recolher
              </Mui.Button>
            )}
          </Mui.Box>
        </>
      )}
    </Mui.Box>
  );

  if (fullScreen)
    return (
      <>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
          ModalProps={{ keepMounted: true }}>
          {content}
        </Drawer>
        <CommandPalette.Menu items={items} isStaff={isStaff} />
      </>
    );

  return (
    <>
      <Mui.Hidden smDown>
        <Drawer anchor="left" classes={{ paper: classes.desktopDrawer }} open variant="persistent">
          {content}
        </Drawer>
      </Mui.Hidden>
      <CommandPalette.Menu items={items} isStaff={isStaff} />
    </>
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  profile: PropTypes.object,
  items: PropTypes.array,
  children: PropTypes.any,
  isNavCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};

export default NavBar;
